import type { ApiClient } from "@hakili/api-client";
import { messageOf } from "@hakili/app-shell";
import { t } from "@hakili/i18n";
import { useCallback, useEffect, useState } from "react";

import { ControlPrevented } from "./control-prevented.js";
import { type SheetAnswer, SheetPage, type SheetState } from "./tour-sheet.js";

/**
 * `round C5-2`, `M2`: **« Ma tournée » on the cabinet's space.**
 *
 * The same VERSION the public link serves for this cabinet's next Thursday,
 * read by `GET /api/v1/cabinet/tour`; opening it acknowledges the version it
 * shows (`POST /api/v1/cabinet/tour/acknowledge`, WF-CON-32 step 5, one
 * acknowledgement per version, as the link's page does); a newer version is
 * announced in words on the screen , until messaging pushes , when the
 * cabinet had read an older one. On the responsable's desk the fee per
 * vehicle is the cabinet's own figure (`GET /api/v1/cabinet/fee`, `OD-29`);
 * on the field expert's phone no fee anywhere, the drawing's sentence saying
 * so (`OD-32`). A vehicle opened on the sheet offers « Contrôle empêché »
 * (decision D); its entry is `M3`'s.
 */

export interface TourAnswer extends SheetAnswer {
  readonly expertiseDayId: string;
  readonly previousAcknowledgedVersion: number | null;
}

type Opened = {
  readonly reference: string;
  readonly demandId: string | null;
  readonly vehicleLabel: string;
} | null;

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

export function MyTour(props: {
  readonly api: ApiClient;
  readonly chair: "expert" | "responsable";
  /** `M3`: the entry, opened from the vehicle's view; `undefined` until it is built. */
  readonly renderEntry?: (opened: NonNullable<Opened>, back: () => void) => React.JSX.Element;
}): React.JSX.Element {
  const { api, chair, renderEntry } = props;
  const [state, setState] = useState<SheetState>({ kind: "loading" });
  const [tour, setTour] = useState<TourAnswer | null>(null);
  const [fee, setFee] = useState<number | null | undefined>(undefined);
  const [opened, setOpened] = useState<Opened>(null);
  const [preventing, setPreventing] = useState(false);
  const [word, setWord] = useState<string | null>(null);

  const read = useCallback(async (): Promise<void> => {
    try {
      const answer = await api.request<{ sheet: TourAnswer | null }>("/api/v1/cabinet/tour", "get");
      if (answer.sheet === null) {
        setTour(null);
        setState({ kind: "refused", message: t("expert.tournee.aucune") });
        return;
      }
      setTour(answer.sheet);
      let acknowledgedAt = answer.sheet.acknowledgedAt;
      if (acknowledgedAt === null) {
        // Opening the sheet in the space acknowledges the version it shows.
        const done = await api.request<{ acknowledgedAt: string }>(
          "/api/v1/cabinet/tour/acknowledge",
          "post",
          {
            body: { version: answer.sheet.version },
          },
        );
        acknowledgedAt = done.acknowledgedAt;
      }
      setState({ kind: "sheet", sheet: answer.sheet, acknowledgedAt });
    } catch (failure) {
      setState({ kind: "refused", message: messageOf(failure, t("common.error.connexionEchouee")) });
    }
  }, [api]);

  useEffect(() => {
    void read();
  }, [read]);

  useEffect(() => {
    if (chair !== "responsable") return;
    api
      .request<{ feeAmount: number | null }>("/api/v1/cabinet/fee", "get")
      .then((answer) => {
        setFee(answer.feeAmount);
      })
      .catch(() => {
        setFee(null);
      });
  }, [api, chair]);

  const post = useCallback(
    async (input: { reason: string; detail: string }): Promise<void> => {
      if (opened?.demandId === null || opened === null) throw new Error(t("expert.empeche.sansDemande"));
      try {
        await api.request<unknown>("/api/v1/cabinet/demands/{id}/prevented", "post", {
          params: { id: opened.demandId },
          body: { reason: input.reason, detail: input.detail === "" ? undefined : input.detail },
        });
      } catch (failure) {
        throw new Error(messageOf(failure, t("common.error.connexionEchouee")), { cause: failure });
      }
      setWord(fill("expert.empeche.signale", { reference: opened.reference }));
      setOpened(null);
      setPreventing(false);
      await read();
    },
    [api, opened, read],
  );

  if (opened !== null && preventing) {
    return (
      <ControlPrevented
        vehicleLabel={opened.vehicleLabel}
        reference={opened.reference}
        back={() => {
          setPreventing(false);
        }}
        post={post}
      />
    );
  }
  if (opened !== null) {
    const back = (): void => {
      setOpened(null);
    };
    return (
      <div className="pb-24" data-testid="vehicule-ouvert">
        <div className="px-5 pb-3 pt-5">
          <button
            type="button"
            onClick={back}
            className="text-xs text-indigo-700"
            data-testid="vehicule-retour"
          >
            {t("expert.empeche.retour")}
          </button>
          <h1 className="mt-1 text-lg font-semibold leading-snug tracking-tight">{opened.vehicleLabel}</h1>
          <p className="text-xs text-gray-500 tabular-nums">{opened.reference}</p>
        </div>
        {renderEntry === undefined ? (
          <div className="mb-3 px-5">
            <p className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-xs text-gray-500">
              {t("expert.tournee.saisieBientot")}
            </p>
          </div>
        ) : (
          renderEntry(opened, back)
        )}
        <div className="px-5">
          <button
            type="button"
            data-testid="vehicule-empecher"
            onClick={() => {
              setPreventing(true);
            }}
            className="w-full rounded-2xl border border-amber-200 bg-amber-50 py-3 text-sm font-medium text-amber-700"
          >
            {t("expert.empeche.titre")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {tour !== null && tour.previousAcknowledgedVersion !== null && tour.acknowledgedAt === null ? (
        <div className="px-5 pt-5">
          <p
            className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-700"
            data-testid="tournee-nouvelle-version"
          >
            {fill("expert.tournee.nouvelleVersion", {
              version: String(tour.version),
              previous: String(tour.previousAcknowledgedVersion),
            })}
          </p>
        </div>
      ) : null}
      {word === null ? null : (
        <div className="px-5 pt-5">
          <p
            className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs text-emerald-800"
            data-testid="tournee-mot"
          >
            {word}
          </p>
        </div>
      )}
      <SheetPage
        state={state}
        space={{
          chair,
          ...(chair === "responsable" ? { feePerVehicle: fee ?? null } : {}),
          onVehicle: (vehicle) => {
            const line = tour?.stops
              .flatMap((stop) => stop.vehicles)
              .find((one) => one.reference === vehicle.reference);
            setOpened({
              reference: vehicle.reference,
              demandId: vehicle.demandId,
              vehicleLabel: line?.vehicleLabel ?? vehicle.reference,
            });
          },
        }}
      />
      <div className="px-5 pb-6">
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
          <p className="text-xs leading-relaxed text-gray-500" data-testid="tournee-note">
            {chair === "expert" ? t("expert.tournee.noteTerrain") : t("expert.tournee.noteBureau")}
          </p>
        </div>
      </div>
    </div>
  );
}
